import { getJackpotWinStatsUrl } from '@repo/common';
import type { IJackpotWinLevel, IJackpotWinRow, IJackpotWinsData } from '../../../features/jackpots';
import { useAppStore } from '../../../store/app/appStore';

interface BeJackpotWin {
  /** Minor units, in the currency the winning player plays in. */
  winAmountPlayerCurrency: number;
  /** Minor units, in the jackpot instance's own currency. */
  winAmountJpCurrency: number;
  /** ISO 8601 UTC timestamp of the win. */
  winTime: string;
  winningGameKey: string;
  /** Already masked server-side, e.g. '06*65'. */
  casinoPlayerId: string;
  playerCurrency: string;
  jackpotCurrency: string;
}

/** Counter and lists are optional on purpose: the mapper must survive a partial payload. */
interface BeJackpotLevelStats {
  levelId: number;
  levelName: string;
  totalWins?: number;
  lastWins?: BeJackpotWin[];
  topWins?: BeJackpotWin[];
}

interface BeInstanceWinStats {
  instanceName?: string;
  levelStats?: BeJackpotLevelStats[];
}

interface BeJackpotWinStatsResponse {
  instanceWinStats?: BeInstanceWinStats[];
}

/**
 * Displayed amounts are the WINNER'S OWN — `winAmountPlayerCurrency` + `playerCurrency` — so a
 * row shows what that player actually received. Ordering ("TOP 10", the header's biggest win)
 * still uses the jackpot-currency amount (`sortAmount`): it is the same currency on every row
 * of a jackpot, so amounts compare like with like — raw numbers across mixed player currencies
 * would not. All amounts stay in BE's minor units end to end; `formatCurrencyValue` converts
 * once, at render.
 */
const mapBeWin = (
  win: BeJackpotWin,
  level: BeJackpotLevelStats,
  supportedGames: Record<string, { name?: string }>,
): IJackpotWinRow => {
  return {
    // BE sends no per-win id; the level, the timestamp, the (masked) player and the amount are
    // the win's natural key and stay stable across refetches. The level segment is the level
    // NAME — `levelId` is 0 on every Premium Link level, so it distinguishes nothing.
    id: `${level.levelName}:${win.winTime}:${win.casinoPlayerId}:${win.winAmountJpCurrency}`,
    // Already masked by BE — passed through untouched.
    playerId: win.casinoPlayerId,
    levelId: level.levelId,
    levelName: level.levelName,
    amount: win.winAmountPlayerCurrency,
    currency: win.playerCurrency,
    sortAmount: win.winAmountJpCurrency,
    // Already ISO 8601; kept verbatim so no timezone information is lost in a round trip.
    wonAt: win.winTime,
    gameKey: win.winningGameKey,
    // BE does not send a display name — resolve from the games catalog, fall back to the key.
    gameName: supportedGames[win.winningGameKey]?.name ?? win.winningGameKey,
  };
};

const mapBeLevel = (
  level: BeJackpotLevelStats,
  supportedGames: Record<string, { name?: string }>,
): IJackpotWinLevel => ({
  levelId: level.levelId,
  levelName: level.levelName,
  totalWins: level.totalWins ?? 0,
  // Ordering is re-established in the drawer's selectors, so nothing here depends on BE's.
  lastWins: (level.lastWins ?? []).map((win) => mapBeWin(win, level, supportedGames)),
  topWins: (level.topWins ?? []).map((win) => mapBeWin(win, level, supportedGames)),
});

const NO_WINS: IJackpotWinsData = { levels: [] };

const normalizeInstanceName = (instanceName: string): string => instanceName.trim().toLowerCase();

const mapJackpotWinStatsResponse = (data: BeJackpotWinStatsResponse, instanceName: string): IJackpotWinsData => {
  const supportedGames = useAppStore.getState().supportedGames;
  const requested = normalizeInstanceName(instanceName);

  // The query asks for one instance but the response is an array. Match by name instead of
  // taking [0], so a response that ever carries more than one jackpot cannot make the drawer
  // show another jackpot's wins under this jackpot's name.
  const instance = (data.instanceWinStats ?? []).find(
    (entry) => normalizeInstanceName(entry.instanceName ?? '') === requested,
  );
  if (!instance) return NO_WINS;

  return { levels: (instance.levelStats ?? []).map((level) => mapBeLevel(level, supportedGames)) };
};

export const fetchJackpotWinStats = async (instanceName: string): Promise<IJackpotWinsData> => {
  const apiHost = useAppStore.getState().settings.apiHost;
  const url = getJackpotWinStatsUrl(apiHost, instanceName);

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch jackpot win stats: ${response.statusText} (URL: ${url})`);
  }

  const data: BeJackpotWinStatsResponse = await response.json();
  return mapJackpotWinStatsResponse(data, instanceName);
};
